import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { Dropdown, ModalShell } from '../../components';
import * as s from './styled-register-variable';
import {
  notifyError,
  notifySuccess,
  notifyWarning,
  sessionCache,
} from '../../utils';
import {
  createVariable,
  getThingsLight,
  getVariableTypes,
  updateVariable,
} from '../../services';
import { resetIotGraphqlDocuments } from '../../graphql/iot-documents';

const unitOptions = [
  { label: 'L/s', value: 'L/s' },
  { label: 'mca', value: 'mca' },
  { label: 'kW', value: 'kW' },
  { label: 'A', value: 'A' },
  { label: 'kWh', value: 'kWh' },
  { label: 'mg', value: 'mg' },
  { label: 'ºC', value: 'ºC' },
  { label: '%', value: '%' },
  { label: 'mm/s', value: 'mm/s' },
  { label: '-', value: '-' },
];

const getOption = (options, value) =>
  options.find((currentOption) => currentOption.value === value) || null;

// Dropdown styles no design system CCO (tokens var(--cco-*)). react-select
// aceita strings de CSS var, entao usamos os mesmos tokens do resto do
// sistema — antes destoava (cores do tema legado).
const buildDropdownStyles = () => ({
  option: (provided, state) => ({
    ...provided,
    padding: 8,
    fontSize: 13,
    backgroundColor: state.isSelected
      ? 'var(--cco-blue)'
      : state.isFocused
      ? 'var(--cco-soft-surface)'
      : 'var(--cco-surface)',
    color: state.isSelected ? '#fff' : 'var(--cco-ink)',
    cursor: 'pointer',
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    paddingLeft: 6,
    fontSize: 13,
    backgroundColor: 'var(--cco-soft-surface)',
    borderColor: state.isFocused ? 'var(--cco-blue)' : 'var(--cco-line)',
    boxShadow: state.isFocused ? '0 0 0 2px var(--cco-blue-soft)' : 'none',
    '&:hover': {
      borderColor: 'var(--cco-line-strong)',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--cco-ink)',
    fontWeight: 600,
  }),
  // Chips das maquinas selecionadas — soft azul + texto azul (estilo CCO).
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'var(--cco-blue-soft)',
    border: '1px solid var(--cco-blue)',
    borderRadius: 999,
    padding: '1px 2px 1px 6px',
    margin: 3,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'var(--cco-blue)',
    fontWeight: 700,
    fontSize: 12,
    padding: 0,
    paddingLeft: 0,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: 'var(--cco-blue)',
    marginLeft: 4,
    borderRadius: 999,
    padding: 2,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'var(--cco-blue)',
      color: '#fff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--cco-surface)',
    border: '1px solid var(--cco-line)',
    zIndex: 1001,
  }),
  menuList: (provided) => ({
    ...provided,
    overflowY: 'auto',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 13,
    color: 'var(--cco-ink-faint)',
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--cco-ink)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorContainer: () => ({
    padding: 5,
  }),
});

// Componente do form de cadastro/edicao de variavel. Funciona em DOIS
// modos:
//   1) Rota (/variables/new) — sem props, le do location.state. Layout
//      fullpage (s.Container + s.HeaderCard + s.FormCard).
//   2) Modal — recebe `asModal`, `variable` (a row a editar; null/omit
//      = modo criacao) e callbacks `onClose` + `onSaved`. Layout vem
//      wrapped em ModalShell e Cancelar/Voltar chamam onClose em vez
//      de navegar.
//
// Mesma logica de submit nos 2 modos; muda so o chrome + para onde
// vai apos sucesso.
const CadastroVariavel = ({
  asModal = false,
  variable: variableProp = null,
  onClose,
  onSaved,
} = {}) => {
  const history = useHistory();
  const { state } = useLocation();
  const { colors } = useContext(ThemeContext);

  const dropdownStyles = useMemo(() => buildDropdownStyles(colors), [colors]);
  const unitDropdownStyles = useMemo(
    () => ({
      ...dropdownStyles,
      menuList: (provided) => ({
        ...dropdownStyles.menuList(provided),
        maxHeight: 176,
      }),
    }),
    [dropdownStyles]
  );
  const thingDropdownStyles = useMemo(
    () => ({
      ...dropdownStyles,
      menu: (provided) => ({
        ...dropdownStyles.menu(provided),
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
      }),
      menuList: (provided) => ({
        ...dropdownStyles.menuList(provided),
        maxHeight: 184,
        paddingRight: 4,
        scrollbarWidth: 'auto',
        scrollbarColor: 'var(--cco-line-strong) var(--cco-soft-surface)',
      }),
    }),
    [dropdownStyles]
  );
  // Quando vem editando, `variable.raw` carrega o objeto original do
  // backend (adaptado em relacao-variaveis). Em modo modal, o variableProp
  // ja vem do parent; em modo rota, vem do location.state.
  const editing = variableProp || (state && state.variable) || null;
  const editingRaw = editing && editing.raw ? editing.raw : editing;

  // Callback unico de "fechar/voltar" — onClose em modo modal, history.push
  // em modo rota. Mesma assinatura em todos os botoes do form.
  const goBack = () => {
    if (onClose) onClose();
    else history.push('/variables');
  };

  const initialForm = useMemo(
    () => ({
      // backend `name` = ihmname (codigo); backend `description` = nome
      // legivel exibido nas tabelas. Pre-populamos do raw quando edita.
      name: editingRaw?.ihmname || editingRaw?.name || '',
      description: editingRaw?.description || '',
      unit: editingRaw?.unit || '',
      // type pode vir como objeto {id, label} ou apenas id no raw.
      typeId:
        (editingRaw?.variableType && editingRaw.variableType.id) ||
        (typeof editingRaw?.type === 'object' ? editingRaw.type.id : null) ||
        null,
      // thingIds: array dos ids das things em que a variavel existe.
      // No raw, vem em `range[]` (cada range tem thing.id).
      thingIds:
        (editingRaw?.range || [])
          .map((r) => r.thing && r.thing.id)
          .filter(Boolean) || [],
    }),
    [editingRaw]
  );

  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [typeList, setTypeList] = useState([]);
  const [thingListItems, setThingListItems] = useState([]);

  // Sincroniza o form quando o initialForm mudar — necessario porque
  // useState(initialForm) so le o valor na PRIMEIRA renderizacao. Sem
  // este effect, abrir o modal pra editar uma row diferente (sem
  // unmount) ou recarregar com props novas deixa o form com os dados
  // antigos / vazios. Idempotente: se initialForm e mesma referencia
  // (useMemo segura), o effect nao re-roda.
  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  // Fetch global de tipos + things — uma vez por sessao via sessionCache.
  useEffect(() => {
    let mounted = true;
    Promise.all([
      sessionCache('variableTypes', 'all', () => getVariableTypes()),
      sessionCache('thingsLight', 'all', () => getThingsLight()),
    ])
      .then(([typesResp, thingsResp]) => {
        if (!mounted) return;
        const typesData =
          (typesResp && typesResp.data && typesResp.data.data) ||
          (typesResp && typesResp.data) ||
          [];
        const thingsData =
          (thingsResp && thingsResp.data && thingsResp.data.data) ||
          (thingsResp && thingsResp.data) ||
          [];
        setTypeList(Array.isArray(typesData) ? typesData : []);
        setThingListItems(Array.isArray(thingsData) ? thingsData : []);
      })
      .catch(() => {
        if (mounted) notifyError('Erro ao carregar tipos e máquinas');
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Memos pras opcoes formatadas pro Dropdown.
  const typeOptions = useMemo(
    () =>
      typeList.map((t) => ({
        // Marca setpoints visualmente pra evitar confusao (mesmo label
        // pode aparecer com isSetpoint=true e false).
        label: t.isSetpoint ? `${t.label} (setpoint)` : t.label,
        value: t.id,
        isSetpoint: t.isSetpoint,
      })),
    [typeList]
  );

  // Opcoes das maquinas JA vinculadas a variavel (do range do editingRaw).
  // Semeamos a lista com elas pra garantir que aparecam SELECIONADAS no
  // dropdown mesmo que getThingsLight nao as inclua / ainda nao tenha
  // carregado — antes o select abria vazio ao editar uma variavel.
  const editingThingOptions = useMemo(
    () =>
      (editingRaw?.range || [])
        .map((r) => r.thing)
        .filter((t) => t && t.id != null)
        .map((t) => ({
          label: `${t.thing || t.name || `Máquina ${t.id}`}${
            t.appliance?.park?.customer?.name
              ? ` · ${t.appliance.park.customer.name}`
              : ''
          }`,
          value: t.id,
        })),
    [editingRaw]
  );

  // Things agrupadas por cliente pra dropdown ficar mais legivel em
  // tenants com muitas maquinas. Une as do getThingsLight com as ja
  // vinculadas (dedup por id) pra o value do dropdown sempre casar.
  const thingOptions = useMemo(() => {
    const byId = new Map();
    thingListItems.forEach((t) => {
      byId.set(t.id, {
        label: `${t.thing || t.name}${
          t.customerName ? ` · ${t.customerName}` : ''
        }`,
        value: t.id,
      });
    });
    editingThingOptions.forEach((opt) => {
      if (!byId.has(opt.value)) byId.set(opt.value, opt);
    });
    return Array.from(byId.values());
  }, [thingListItems, editingThingOptions]);

  const handleChange = (field, value) => {
    setForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (
      !form.name ||
      !form.description ||
      !form.typeId ||
      !form.unit ||
      !form.thingIds.length
    ) {
      notifyWarning(
        'Preencha nome, descrição, tipo, unidade e pelo menos uma máquina.'
      );
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        description: form.description,
        unit: form.unit,
        type: Number(form.typeId),
        // Backend aceita array; se for 1 thing, manda mesmo assim
        // como array — endpoint trata os 2 casos.
        thingId: form.thingIds.length === 1 ? form.thingIds[0] : form.thingIds,
      };
      if (editing) {
        await updateVariable(editing.id, body);
        notifySuccess('Variável atualizada com sucesso!');
      } else {
        await createVariable(body);
        notifySuccess('Variável cadastrada com sucesso!');
      }
      resetIotGraphqlDocuments();
      // Em modo modal: avisa o parent (geralmente pra recarregar lista) e
      // fecha o modal. Em modo rota: cai no goBack que faz history.push.
      if (onSaved) onSaved();
      goBack();
    } catch (err) {
      notifyError(
        editing ? 'Erro ao atualizar variável.' : 'Erro ao cadastrar variável.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Conteudo do form — extraido pra reuso entre modo rota e modal.
  // Reutiliza s.FormCard pra preservar o estilo visual do card de form
  // (border, padding, etc) tanto na pagina inteira quanto dentro do
  // ModalShell.
  const formBody = (
    <s.FormCard>
      <s.Grid>
        <s.Field>
          <s.Label>Nome da variável *</s.Label>
          <s.FormInput
            placeholder="Ex.: TEMP_SENSOR_01"
            value={form.name}
            onChange={(event) => handleChange('name', event.target.value)}
          />
        </s.Field>

        <s.Field>
          <s.Label>Descrição *</s.Label>
          <s.FormInput
            placeholder="Ex.: Temperatura do mancal"
            value={form.description}
            onChange={(event) =>
              handleChange('description', event.target.value)
            }
          />
        </s.Field>

        <s.Field>
          <s.Label>Tipo *</s.Label>
          <Dropdown
            style={dropdownStyles}
            options={typeOptions}
            value={getOption(typeOptions, form.typeId)}
            onChange={(selectedOption) =>
              handleChange('typeId', selectedOption?.value ?? null)
            }
            placeholder="Selecione"
            isSearchable={false}
          />
        </s.Field>

        <s.Field>
          <s.Label>Unidade de medida *</s.Label>
          <Dropdown
            style={unitDropdownStyles}
            options={unitOptions}
            value={getOption(unitOptions, form.unit)}
            onChange={(selectedOption) =>
              handleChange('unit', selectedOption?.value || '')
            }
            placeholder="Selecione"
            isSearchable={false}
          />
        </s.Field>

        <s.Field style={{ gridColumn: '1 / -1' }}>
          <s.Label>Seleção de Máquina(s) *</s.Label>
          <Dropdown
            style={thingDropdownStyles}
            menuPlacement="auto"
            options={thingOptions}
            value={thingOptions.filter((opt) =>
              form.thingIds.includes(opt.value)
            )}
            onChange={(selectedOptions) =>
              handleChange(
                'thingIds',
                (selectedOptions || []).map((o) => o.value)
              )
            }
            placeholder={
              thingOptions.length
                ? 'Digite para buscar uma máquina'
                : 'Carregando máquinas...'
            }
            isSearchable
            isMulti
          />
        </s.Field>
      </s.Grid>

      {/* Modo modal: SALVAR centralizado, sem CANCELAR (o X do
            ModalShell fecha). Modo rota: CANCELAR + PRIMARY a direita,
            com texto contextual ("Cadastrar"/"Salvar alteracoes"). */}
      {asModal ? (
        <s.Actions $center>
          <s.ModalSaveButton onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'SALVANDO...' : 'SALVAR'}
          </s.ModalSaveButton>
        </s.Actions>
      ) : (
        <s.Actions>
          <s.SecondaryButton onClick={goBack}>CANCELAR</s.SecondaryButton>
          <s.PrimaryButton onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? 'SALVANDO...'
              : editing
              ? 'SALVAR ALTERAÇÕES'
              : 'CADASTRAR VARIÁVEL'}
          </s.PrimaryButton>
        </s.Actions>
      )}
    </s.FormCard>
  );

  // Modo modal: ModalShell prove overlay + container centralizado +
  // botao de fechar (X) + Title/Subtitle. Largura 900px acomoda os
  // 2 campos por linha do Grid sem espremer; em mobile reduz via
  // overflow do proprio Overlay.
  if (asModal) {
    return (
      <ModalShell
        title={editing ? 'Editar Variável' : 'Cadastro de Variável'}
        subtitle={
          editing
            ? 'Atualize os dados da variável selecionada.'
            : 'Preencha os campos para inserir uma nova variável no supervisório.'
        }
        requiredHint="*itens obrigatórios"
        width="min(900px, calc(100vw - 32px))"
        onRequestClose={goBack}
      >
        {formBody}
      </ModalShell>
    );
  }

  // Modo rota: layout fullpage com HeaderCard proprio.
  return (
    <s.Container className="fade-in">
      <s.HeaderCard>
        <s.TitleGroup>
          <s.Title>
            {editing ? 'Editar Variável' : 'Cadastro de Variável'}
          </s.Title>
          <s.SubTitle>
            {editing
              ? 'Atualize os dados da variável selecionada.'
              : 'Preencha os campos para inserir uma nova variável no supervisório.'}
          </s.SubTitle>
        </s.TitleGroup>

        <s.SecondaryButton onClick={goBack}>
          VOLTAR PARA LISTA
        </s.SecondaryButton>
      </s.HeaderCard>

      {formBody}
    </s.Container>
  );
};

export default CadastroVariavel;
